import React, { useState, useEffect } from "react";
import { ChevronDown, ChevronUp, ChevronRight, X, Plus, Trash2, Settings, RefreshCw, CheckCircle, AlertTriangle, Database, Users, Activity, Filter, Flag, Info, Target } from "lucide-react";
import { SH, Inp, Card, Badge, SetupBanner, PROXY, fmtM, fmtX } from "../shared/constants";
import { fetchPipelineStages, fetchOwners } from "../shared/hubspotHelpers";
import { OwnerTypeahead } from "../shared/components";

const SegmentAttributionMapper = ({ s, upd }) => {
  const [properties,    setProperties]    = useState([]);
  const [propValues,    setPropValues]    = useState([]);
  const [loadingProps,  setLoadingProps]  = useState(false);
  const [loadingVals,   setLoadingVals]   = useState(false);
  const [error,         setError]         = useState("");
  const mode = s.segments?.attribution?.mode || "rep";
  const selectedProp = s.segments?.attribution?.property || "";
  const valueMap = s.segments?.attribution?.valueMap || {};
  const segNames = (s.segments?.list || []).map(sg => sg.name);

  const loadProperties = async () => {
    if (!s.hubspot?.token) { setError("Connect HubSpot first."); return; }
    setLoadingProps(true); setError("");
    try {
      const res = await fetch(`${PROXY}?endpoint=deal_properties`, { headers: { Authorization: `Bearer ${s.hubspot.token}` } });
      const data = await res.json();
      // Filter to enumeration + string properties that are meaningful
      const filtered = (data.results || [])
        .filter(p => ["enumeration","string","radio"].includes(p.type) && !p.hidden && !p.calculated)
        .sort((a,b) => a.label.localeCompare(b.label));
      setProperties(filtered);
    } catch(e) { setError(e.message); }
    finally { setLoadingProps(false); }
  };

  const loadValues = async (propName) => {
    if (!s.hubspot?.token || !propName) return;
    setLoadingVals(true);
    try {
      const res = await fetch(`${PROXY}?endpoint=property_values&propertyName=${propName}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${s.hubspot.token}`, "Content-Type": "application/json" }
      });
      const data = await res.json();
      // Get unique values from results
      const vals = [...new Set((data.results || []).map(d => d.properties?.[propName]).filter(Boolean))].sort();
      // Also check property options if available
      const prop = properties.find(p => p.name === propName);
      const optionVals = (prop?.options || []).map(o => o.value);
      const allVals = [...new Set([...vals, ...optionVals])].filter(Boolean).sort();
      setPropValues(allVals);
    } catch(e) { setPropValues([]); }
    finally { setLoadingVals(false); }
  };

  const setMode = (m) => upd("segments.attribution", { ...s.segments?.attribution, mode: m });
  const setProp = (p) => {
    upd("segments.attribution", { ...s.segments?.attribution, property: p, valueMap: {} });
    loadValues(p);
  };
  const mapValue = (val, seg) => {
    const newMap = { ...valueMap, [val]: seg };
    upd("segments.attribution", { ...s.segments?.attribution, valueMap: newMap });
  };

  return (
    <div style={{marginTop:20,borderTop:"1px solid rgba(0,0,0,0.08)",paddingTop:20}}>
      <SH title="Segment Attribution" sub="How should deals be attributed to segments?"/>

      {/* Mode selector */}
      <div style={{display:"flex",gap:8,marginBottom:16}}>
        {[
          { id:"rep",      label:"By Rep",             desc:"Deals go to the segment of their owner" },
          { id:"property", label:"By HubSpot Property", desc:"Use a deal field to determine segment" },
        ].map(m => (
          <button key={m.id} onClick={() => setMode(m.id)}
            style={{flex:1,padding:"10px 14px",borderRadius:8,border:`1px solid ${mode===m.id?"#CC1A1A":"rgba(0,0,0,0.08)"}`,
              background:mode===m.id?"rgba(204,26,26,0.10)":"transparent",cursor:"pointer",textAlign:"left"}}>
            <div style={{fontSize:12,fontWeight:700,color:mode===m.id?"#CC1A1A":"#111111"}}>{m.label}</div>
            <div style={{fontSize:10,color:"#555555",marginTop:2}}>{m.desc}</div>
          </button>
        ))}
      </div>

      {mode === "property" && (
        <>
          {/* Property picker */}
          <div style={{marginBottom:12}}>
            {!properties.length ? (
              <button onClick={loadProperties} disabled={loadingProps}
                style={{background:"linear-gradient(135deg,#CC1A1A,#991B1B)",border:"none",borderRadius:8,padding:"9px 18px",color:"#F8F8F8",fontSize:12,fontWeight:700,cursor:"pointer",opacity:loadingProps?0.7:1}}>
                {loadingProps ? "Loading properties…" : "🔄 Load HubSpot Deal Properties"}
              </button>
            ) : (
              <div>
                <div style={{fontSize:11,fontWeight:700,color:"#555555",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:6}}>Select the deal property that identifies segment</div>
                <select value={selectedProp} onChange={e => setProp(e.target.value)}
                  style={{width:"100%",background:"#F0F0F0",border:"1px solid rgba(204,26,26,0.10)",borderRadius:7,padding:"9px 12px",color:"#111111",fontSize:13,outline:"none"}}>
                  <option value="">— pick a property —</option>
                  {properties.map(p => (
                    <option key={p.name} value={p.name}>{p.label} ({p.name})</option>
                  ))}
                </select>
              </div>
            )}
            {error && <div style={{fontSize:11,color:"#DC2626",marginTop:6}}>{error}</div>}
          </div>

          {/* Value → segment mapping */}
          {selectedProp && (
            <div>
              <div style={{fontSize:11,fontWeight:700,color:"#555555",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>
                Map each "{selectedProp}" value to a RevXcel segment
              </div>
              {loadingVals ? (
                <div style={{fontSize:12,color:"#555555"}}>Loading values…</div>
              ) : propValues.length > 0 ? (
                propValues.map(val => (
                  <div key={val} style={{display:"grid",gridTemplateColumns:"1fr 200px",gap:12,alignItems:"center",marginBottom:8,padding:"8px 12px",background:"#FFFFFF",borderRadius:7}}>
                    <div style={{fontSize:12,color:"#111111",fontFamily:"'DM Mono',monospace"}}>{val}</div>
                    <select value={valueMap[val] || ""} onChange={e => mapValue(val, e.target.value)}
                      style={{background:"#F0F0F0",border:`1px solid ${valueMap[val]?"rgba(22,163,74,0.12)":"rgba(204,26,26,0.10)"}`,borderRadius:6,padding:"7px 10px",color:valueMap[val]?"#16A34A":"#555555",fontSize:12,outline:"none"}}>
                      <option value="">— ignore —</option>
                      {[...segNames, ...(!segNames.includes("Renewal") ? ["Renewal"] : [])].map(sg => (
                        <option key={sg} value={sg}>{sg}</option>
                      ))}
                    </select>
                  </div>
                ))
              ) : (
                <div style={{fontSize:12,color:"#555555"}}>No values found for this property. Try a different one.</div>
              )}
              {propValues.length > 0 && (
                <div style={{marginTop:8,fontSize:11,color:"#555555"}}>
                  {Object.values(valueMap).filter(Boolean).length} of {propValues.length} values mapped
                </div>
              )}
            </div>
          )}
        </>
      )}

      {mode === "rep" && (
        <div style={{padding:"10px 14px",background:"rgba(204,26,26,0.10)",borderRadius:8,fontSize:12,color:"#CC1A1A"}}>
          Deals will be attributed to the segment of their HubSpot owner. Make sure each rep is linked to an owner in <strong>Quota & Team</strong> above.
        </div>
      )}
    </div>
  );
};


// ── Field groups: the canonical set of HubSpot fields RevXcel needs ──────────
// Grouped by purpose. Each field has: key (maps to settings.fieldMap[key]),
// label (shown in UI), hint (tooltip), required flag, and the HubSpot default.
const FIELD_GROUPS = [
  {
    id: "core", label: "Deal Core", icon: "💼", color: "#CC1A1A",
    desc: "Required for all revenue math — bookings, pipeline, win rate, coverage",
    fields: [
      { key:"dealname",  label:"Deal / Opportunity Name",  default:"dealname",           required:true,  hint:"Shown in deal lists and tooltips across all screens." },
      { key:"dealstage", label:"Deal Stage",               default:"dealstage",           required:true,  hint:"Classified as Won / Lost / Pipeline via Stage Mapping." },
      { key:"amount",    label:"Amount (ACV / ARR)",       default:"amount",              required:true,  hint:"Dollar value — drives all pipeline and bookings math." },
      { key:"closedate", label:"Expected Close Date",      default:"closedate",           required:true,  hint:"Period bucketing, stale detection, pace calculation." },
      { key:"createdate",label:"Created Date",             default:"createdate",          required:true,  hint:"Deal age and average sales cycle calculation." },
      { key:"owner",     label:"Owner (Rep)",              default:"hubspot_owner_id",    required:true,  hint:"Rep-level quota attainment, coaching, and pipeline attribution." },
    ],
  },
  {
    id: "enrichment", label: "Forecasting & Segmentation", icon: "🎯", color: "#7C3AED",
    desc: "Needed for weighted pipeline, motion segmentation, and multi-pipeline portals",
    fields: [
      { key:"forecastCategory", label:"Forecast Category",    default:"hs_forecast_category",         required:false, hint:"Commit / Best Case / Pipeline / Omitted. Weighted forecast." },
      { key:"dealtype",         label:"Motion Type",          default:"dealtype",                     required:false, hint:"New Logo / Upsell / Renewal — revenue matrix row attribution." },
      { key:"probability",      label:"Win Probability (%)",  default:"hs_deal_stage_probability",    required:false, hint:"Stage-based probability for weighted pipeline value." },
      { key:"pipeline",         label:"Pipeline",             default:"pipeline",                     required:false, hint:"Multi-pipeline portals: filters deals to the right pipeline." },
    ],
  },
  {
    id: "health", label: "CRM Health", icon: "🔍", color: "#D97706",
    desc: "Signals used to score data quality and surface at-risk deals",
    fields: [
      { key:"lastActivity",  label:"Last Activity Date",       default:"notes_last_activity_date", required:false, hint:"Stale deal detection — flags deals dark for 30+ days." },
      { key:"lastModified",  label:"Last Modified Date",       default:"hs_lastmodifieddate",       required:false, hint:"Recency signal when last activity date is unavailable." },
      { key:"nextStep",      label:"Next Step",                default:"hs_next_step",              required:false, hint:"Flags deals with no documented next action." },
      { key:"numContacts",   label:"Number of Contacts",       default:"num_associated_contacts",   required:false, hint:"CRM Health completeness — flags single-threaded deals." },
    ],
  },
  {
    id: "account", label: "Account Coverage", icon: "🏢", color: "#16A34A",
    desc: "Company object fields used in the Account Coverage screen",
    fields: [
      { key:"acctOwner",         label:"Account Owner",         default:"hubspot_owner_id",         required:false, hint:"Territory assignment and rep coverage mapping." },
      { key:"acctLastActivity",  label:"Last Activity Date",    default:"notes_last_activity_date", required:false, hint:"Dark account detection — no activity in 90+ days." },
      { key:"acctLastContacted", label:"Last Contacted Date",   default:"notes_last_contacted",     required:false, hint:"Outreach recency for coverage scoring." },
      { key:"acctOpenDeals",     label:"Open Deals Count",      default:"hs_num_open_deals",        required:false, hint:"Accounts with no open deals = unworked territory." },
      { key:"acctLifecycle",     label:"Lifecycle Stage",       default:"lifecyclestage",           required:false, hint:"Customer vs prospect segmentation in coverage view." },
      { key:"acctArr",           label:"Annual Revenue / ARR",  default:"annualrevenue",            required:false, hint:"Account sizing for coverage prioritization." },
    ],
  },
];

// ── Field Mapper Component ─────────────────────────
const FieldMapper = ({ s, upd }) => {
  const [activeGroup, setActiveGroup] = useState("core");
  const [properties,  setProperties]  = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState("");
  const [openKey,     setOpenKey]     = useState(null);
  const [search,      setSearch]      = useState("");
  const fm = s.fieldMap || {};

  const loadProps = async () => {
    if (!s.hubspot?.token) { setError("Add your HubSpot token first (HubSpot Connection tab)."); return; }
    setLoading(true); setError("");
    try {
      // Deals endpoint for Groups 1-3, companies endpoint for Group 4
      const endpoints = activeGroup === "account" ? ["account_properties"] : ["deal_properties"];
      const results = await Promise.all(endpoints.map(ep =>
        fetch(`${PROXY}?endpoint=${ep}`, { headers:{ Authorization:`Bearer ${s.hubspot.token}` }})
          .then(r => r.json()).then(d => d.results || [])
      ));
      const props = results.flat()
        .map(p => ({ name:p.name, label:p.label, type:p.type, fieldType:p.fieldType }))
        .sort((a,b) => a.label.localeCompare(b.label));
      setProperties(props);
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const setField = (key, val) => {
    upd("fieldMap", { ...fm, [key]: val, configured: true });
    setOpenKey(null); setSearch("");
  };

  const group     = FIELD_GROUPS.find(g => g.id === activeGroup);
  const allFields = FIELD_GROUPS.flatMap(g => g.fields);
  const coreReq   = FIELD_GROUPS.find(g => g.id === "core").fields;
  const coreMapped = coreReq.filter(f => fm[f.key]).length;

  return (
    <>
      <SH title="Field Mapping"
        sub="Map RevXcel's logical field names to your HubSpot property names. Defaults work for standard portals — only change if you use custom properties." />

      {/* Status bar */}
      <div style={{display:"flex",gap:10,marginBottom:16,flexWrap:"wrap"}}>
        <div style={{padding:"8px 14px",borderRadius:8,background: coreMapped===coreReq.length?"rgba(22,163,74,0.08)":"rgba(220,38,38,0.06)",
          border:`1px solid ${coreMapped===coreReq.length?"rgba(22,163,74,0.20)":"rgba(220,38,38,0.20)"}`}}>
          <span style={{fontSize:11,fontWeight:700,color:coreMapped===coreReq.length?"#16A34A":"#DC2626"}}>
            {coreMapped===coreReq.length?"✓":"⚠"} Core fields: {coreMapped}/{coreReq.length} mapped
          </span>
        </div>
        <div style={{padding:"8px 14px",borderRadius:8,background:"rgba(0,0,0,0.03)",border:"1px solid rgba(0,0,0,0.08)"}}>
          <span style={{fontSize:11,color:"#555"}}>
            {allFields.filter(f => fm[f.key]).length}/{allFields.length} total fields mapped
          </span>
        </div>
        <div style={{marginLeft:"auto",padding:"8px 14px",borderRadius:8,background:"rgba(0,0,0,0.03)",border:"1px solid rgba(0,0,0,0.08)"}}>
          <span style={{fontSize:11,color:"#888"}}>
            💡 Pre-filled with HubSpot defaults. Only change if your portal uses custom property names.
          </span>
        </div>
      </div>

      {/* Group tabs */}
      <div style={{display:"flex",gap:6,marginBottom:16,flexWrap:"wrap"}}>
        {FIELD_GROUPS.map(g => {
          const active  = activeGroup === g.id;
          const mapped  = g.fields.filter(f => fm[f.key]).length;
          const total   = g.fields.length;
          return (
            <button key={g.id} onClick={()=>{ setActiveGroup(g.id); setProperties([]); setOpenKey(null); }}
              style={{display:"flex",alignItems:"center",gap:7,padding:"8px 14px",borderRadius:8,cursor:"pointer",
                border:`1px solid ${active?g.color:"rgba(0,0,0,0.08)"}`,
                background:active?`${g.color}12`:"transparent",
                color:active?g.color:"#555",fontSize:12,fontWeight:active?700:400}}>
              <span>{g.icon}</span>
              <span>{g.label}</span>
              <span style={{fontSize:9,background:mapped===total?"rgba(22,163,74,0.12)":active?`${g.color}20`:"rgba(0,0,0,0.06)",
                color:mapped===total?"#16A34A":active?g.color:"#888",borderRadius:10,padding:"1px 7px",fontWeight:700}}>
                {mapped}/{total}
              </span>
            </button>
          );
        })}
      </div>

      {/* Active group */}
      {group && (
        <div>
          {/* Header row */}
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12,
            padding:"10px 16px",background:`${group.color}08`,border:`1px solid ${group.color}20`,borderRadius:8}}>
            <div>
              <span style={{fontSize:13,fontWeight:700,color:group.color}}>{group.icon} {group.label}</span>
              <span style={{fontSize:11,color:"#666",marginLeft:10}}>{group.desc}</span>
            </div>
            <button onClick={loadProps} disabled={loading}
              style={{padding:"6px 14px",background:properties.length?"rgba(22,163,74,0.10)":"rgba(0,0,0,0.06)",
                border:`1px solid ${properties.length?"rgba(22,163,74,0.20)":"rgba(0,0,0,0.10)"}`,borderRadius:6,
                color:properties.length?"#16A34A":"#555",fontSize:11,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"}}>
              {loading?"⏳ Loading…" : properties.length?`✓ ${properties.length} props loaded`:"⟳ Load HubSpot Props"}
            </button>
          </div>
          {error && <div style={{marginBottom:10,padding:"8px 12px",background:"rgba(220,38,38,0.08)",borderRadius:6,fontSize:11,color:"#DC2626"}}>{error}</div>}

          {/* Field table */}
          <div style={{border:"1px solid rgba(0,0,0,0.08)",borderRadius:10,overflow:"visible"}}>
            {/* Table header */}
            <div style={{display:"grid",gridTemplateColumns:"200px 1fr 280px",background:"#F0F0F0",
              padding:"8px 16px",borderBottom:"1px solid rgba(0,0,0,0.08)",borderRadius:"10px 10px 0 0"}}>
              {["RevXcel Field","What it powers","Mapped HubSpot Property"].map((h,i)=>(
                <div key={i} style={{fontSize:10,fontWeight:700,color:"#666",textTransform:"uppercase",letterSpacing:"0.06em"}}>{h}</div>
              ))}
            </div>

            {group.fields.map((field, fi) => {
              const mapped     = fm[field.key] || field.default;
              const propMatch  = properties.find(p => p.name === mapped);
              const isOpen     = openKey === field.key;
              const filtered   = properties.filter(p =>
                p.label.toLowerCase().includes(search.toLowerCase()) ||
                p.name.toLowerCase().includes(search.toLowerCase())
              );
              const isDefault  = mapped === field.default;
              const isCustom   = !isDefault && mapped;

              return (
                <div key={field.key}
                  style={{display:"grid",gridTemplateColumns:"200px 1fr 280px",
                    padding:"11px 16px",alignItems:"center",
                    borderBottom:fi<group.fields.length-1?"1px solid rgba(0,0,0,0.04)":"none",
                    background:fi%2?"rgba(0,0,0,0.01)":"transparent"}}>

                  {/* Field label */}
                  <div>
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                      <span style={{fontSize:12,fontWeight:600,color:"#111"}}>{field.label}</span>
                      {field.required && <span style={{fontSize:9,background:"rgba(220,38,38,0.10)",color:"#DC2626",borderRadius:4,padding:"1px 5px",fontWeight:700}}>REQUIRED</span>}
                    </div>
                    {isCustom && <span style={{fontSize:9,color:"#7C3AED",fontFamily:"'DM Mono',monospace"}}>{field.default} → custom</span>}
                    {isDefault && <span style={{fontSize:9,color:"#AAA",fontFamily:"'DM Mono',monospace"}}>{field.default}</span>}
                  </div>

                  {/* Hint */}
                  <div style={{fontSize:11,color:"#666",paddingRight:20,lineHeight:1.5}}>{field.hint}</div>

                  {/* Dropdown picker */}
                  <div style={{position:"relative"}}>
                    <button onClick={()=>{ setOpenKey(isOpen?null:field.key); setSearch(""); }}
                      style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",
                        background: isCustom?"rgba(124,58,237,0.06)" : mapped?"rgba(22,163,74,0.05)":"#F8F8F8",
                        border:`1px solid ${isCustom?"rgba(124,58,237,0.25)" : mapped?"rgba(22,163,74,0.20)":"rgba(0,0,0,0.10)"}`,
                        borderRadius:7,padding:"7px 10px",
                        color: isCustom?"#7C3AED" : mapped?"#16A34A":"#999",
                        fontSize:11,cursor:"pointer",textAlign:"left",gap:6,minHeight:34}}>
                      <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1,fontFamily:"'DM Mono',monospace"}}>
                        {mapped
                          ? <>{propMatch?.label && <span style={{color:"#555",fontSize:10,fontFamily:"sans-serif",marginRight:4}}>{propMatch.label}</span>}{mapped}</>
                          : <span style={{fontStyle:"italic",fontFamily:"sans-serif",color:"#AAA"}}>{properties.length?"— select —":"load props first"}</span>
                        }
                      </span>
                      <div style={{display:"flex",gap:5,flexShrink:0,alignItems:"center"}}>
                        {isCustom && <span onClick={e=>{e.stopPropagation();setField(field.key,field.default);}}
                          title="Reset to default" style={{color:"#7C3AED",fontSize:11,cursor:"pointer",fontWeight:700}}>↺</span>}
                        <ChevronDown size={11} style={{transform:isOpen?"rotate(180deg)":"none",transition:"0.2s",color:"#AAA"}}/>
                      </div>
                    </button>

                    {isOpen && (
                      <div style={{position:"absolute",top:"calc(100% + 4px)",left:0,right:0,zIndex:400,
                        background:"#FFF",border:"1px solid rgba(0,0,0,0.12)",borderRadius:8,
                        boxShadow:"0 8px 32px rgba(0,0,0,0.15)",overflow:"hidden"}}>
                        <div style={{padding:"7px 8px",borderBottom:"1px solid rgba(0,0,0,0.06)"}}>
                          <input autoFocus value={search} onChange={e=>setSearch(e.target.value)}
                            placeholder="Search label or property key…"
                            style={{width:"100%",background:"#F8F8F8",border:"1px solid rgba(0,0,0,0.10)",
                              borderRadius:5,padding:"6px 8px",color:"#111",fontSize:11,outline:"none",boxSizing:"border-box"}}/>
                        </div>
                        {/* Reset to default option */}
                        <div onClick={()=>setField(field.key, field.default)}
                          style={{padding:"7px 12px",fontSize:11,cursor:"pointer",borderBottom:"1px solid rgba(0,0,0,0.04)",
                            display:"flex",justifyContent:"space-between",alignItems:"center"}}
                          onMouseEnter={e=>e.currentTarget.style.background="rgba(0,0,0,0.03)"}
                          onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                          <span style={{color:"#555"}}>Use default</span>
                          <span style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"#888"}}>{field.default}</span>
                        </div>
                        <div style={{maxHeight:220,overflowY:"auto"}}>
                          {properties.length===0 && <div style={{padding:14,fontSize:11,color:"#999",textAlign:"center"}}>Click "Load HubSpot Props" above first</div>}
                          {properties.length>0 && filtered.length===0 && <div style={{padding:14,fontSize:11,color:"#999",textAlign:"center"}}>No matches for "{search}"</div>}
                          {filtered.map(p => {
                            const sel = mapped === p.name;
                            return (
                              <div key={p.name} onClick={()=>setField(field.key, p.name)}
                                style={{padding:"8px 12px",cursor:"pointer",
                                  background:sel?"rgba(22,163,74,0.08)":"transparent",
                                  borderBottom:"1px solid rgba(0,0,0,0.03)"}}
                                onMouseEnter={e=>{ if(!sel) e.currentTarget.style.background="rgba(0,0,0,0.03)"; }}
                                onMouseLeave={e=>{ if(!sel) e.currentTarget.style.background="transparent"; }}>
                                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
                                  <div style={{flex:1,minWidth:0}}>
                                    <span style={{fontSize:12,fontWeight:sel?700:400,color:sel?"#16A34A":"#111"}}>{p.label}</span>
                                    <span style={{fontSize:10,color:"#999",marginLeft:8,fontFamily:"'DM Mono',monospace"}}>{p.name}</span>
                                  </div>
                                  <div style={{display:"flex",gap:4,flexShrink:0,alignItems:"center"}}>
                                    <span style={{fontSize:9,color:"#888",background:"rgba(0,0,0,0.06)",borderRadius:3,padding:"1px 5px"}}>{p.fieldType||p.type}</span>
                                    {sel && <span style={{color:"#16A34A",fontSize:12,fontWeight:700}}>✓</span>}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};

const SegmentGoalsSection = ({ s, upd }) => {
  const [inputMode, setInputMode] = useState("dollar"); // "dollar" | "percent"
  const annual = +s.revenue?.target || 0;
  const cols = ["#CC1A1A","#D97706","#7C3AED","#16A34A","#C2610C"];
  const list = s.segments?.list || [];
  const total = list.reduce((a,sg) => a + (+sg.goal||0), 0);
  const balanced = annual > 0 && Math.abs(total - annual) < 100;

  const updGoal = (i, val) => {
    const l = [...list];
    l[i] = { ...l[i], goal: val };
    upd("segments.list", l);
    upd("segments.configured", true);
  };

  const updPct = (i, pct) => {
    if (!annual) return;
    const val = Math.round(annual * (+pct||0) / 100);
    updGoal(i, String(val));
  };

  const splitEvenly = () => {
    if (!annual || !list.length) return;
    const base = Math.floor(annual / list.length);
    const remainder = annual - base * list.length;
    const l = list.map((sg, i) => ({ ...sg, goal: String(base + (i===0 ? remainder : 0)) }));
    upd("segments.list", l);
    upd("segments.configured", true);
  };

  const autoFillLast = () => {
    if (!annual || list.length < 2) return;
    const filledTotal = list.slice(0,-1).reduce((a,sg)=>a+(+sg.goal||0),0);
    const remaining = annual - filledTotal;
    if (remaining < 0) return;
    const l = [...list];
    l[l.length-1] = { ...l[l.length-1], goal: String(remaining) };
    upd("segments.list", l);
    upd("segments.configured", true);
  };

  return (
    <>
      <SH title="Segment Goals" sub="Split your annual revenue target across segments"/>

      {/* No annual target warning */}
      {!annual && (
        <div style={{padding:"10px 14px",background:"rgba(217,119,6,0.12)",border:"1px solid rgba(217,119,6,0.12)",borderRadius:8,fontSize:12,color:"#D97706",marginBottom:14}}>
          ⚠ Set your <strong>Annual Revenue Target</strong> first — then come back here to split it.
        </div>
      )}

      {/* Annual target display */}
      {annual > 0 && (
        <div style={{padding:"10px 16px",background:"rgba(204,26,26,0.10)",borderRadius:8,marginBottom:14,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <span style={{fontSize:12,color:"#555555"}}>Annual Target to split:</span>
          <span style={{fontSize:18,fontWeight:800,color:"#CC1A1A",fontFamily:"'DM Mono',monospace"}}>{fmtM(annual)}</span>
        </div>
      )}

      {/* Input mode toggle + quick actions */}
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14,flexWrap:"wrap"}}>
        <div style={{display:"flex",gap:4}}>
          {[{id:"dollar",l:"$ Amount"},{id:"percent",l:"% Split"}].map(m=>(
            <button key={m.id} onClick={()=>setInputMode(m.id)}
              style={{padding:"5px 12px",borderRadius:6,border:`1px solid ${inputMode===m.id?"#CC1A1A":"rgba(0,0,0,0.08)"}`,
                background:inputMode===m.id?"rgba(0,0,0,0.08)":"transparent",
                color:inputMode===m.id?"#CC1A1A":"#555555",fontSize:11,fontWeight:600,cursor:"pointer"}}>
              {m.l}
            </button>
          ))}
        </div>
        {annual > 0 && (
          <>
            <button onClick={splitEvenly}
              style={{padding:"5px 12px",borderRadius:6,background:"transparent",border:"1px solid rgba(0,0,0,0.08)",color:"#555555",fontSize:11,cursor:"pointer"}}
              onMouseEnter={e=>{e.target.style.color="#CC1A1A";e.target.style.borderColor="#CC1A1A"}}
              onMouseLeave={e=>{e.target.style.color="#555555";e.target.style.borderColor="rgba(0,0,0,0.08)"}}>
              ÷ Split Evenly
            </button>
            {list.length > 1 && list.slice(0,-1).every(sg=>+sg.goal>0) && !+list[list.length-1].goal && (
              <button onClick={autoFillLast}
                style={{padding:"5px 12px",borderRadius:6,background:"rgba(22,163,74,0.12)",border:"1px solid rgba(22,163,74,0.12)",color:"#16A34A",fontSize:11,cursor:"pointer",fontWeight:600}}>
                ✦ Auto-fill remainder
              </button>
            )}
          </>
        )}
      </div>

      {/* Segment rows */}
      <div style={{marginBottom:10}}>
        {/* Column headers */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 160px 100px 36px",gap:10,marginBottom:6,padding:"0 2px"}}>
          <div style={{fontSize:10,fontWeight:700,color:"#555555",textTransform:"uppercase",letterSpacing:"0.06em"}}>Segment Name</div>
          <div style={{fontSize:10,fontWeight:700,color:"#555555",textTransform:"uppercase",letterSpacing:"0.06em"}}>{inputMode==="dollar"?"Goal ($)":"Split (%)"}</div>
          <div style={{fontSize:10,fontWeight:700,color:"#555555",textTransform:"uppercase",letterSpacing:"0.06em"}}>Amount</div>
          <div/>
        </div>

        {list.map((seg,i)=>{
          const pct = annual && +seg.goal ? Math.round(+seg.goal/annual*100) : "";
          const barPct = total > 0 ? (+seg.goal||0)/total*100 : 0;
          return (
            <div key={i} style={{display:"grid",gridTemplateColumns:"1fr 160px 100px 36px",gap:10,alignItems:"center",marginBottom:8}}>
              {/* Name */}
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <div style={{width:10,height:10,borderRadius:"50%",background:seg.color||cols[i%cols.length],flexShrink:0}}/>
                <input value={seg.name||""} placeholder="e.g. New Logo"
                  onChange={e=>{const l=[...list];l[i]={...l[i],name:e.target.value};upd("segments.list",l);}}
                  style={{flex:1,background:"transparent",border:"none",borderBottom:"1px solid rgba(204,26,26,0.10)",color:"#111111",fontSize:13,fontWeight:600,outline:"none",padding:"4px 0"}}/>
              </div>

              {/* Dollar or Percent input */}
              {inputMode==="dollar" ? (
                <input type="number" value={seg.goal||""} placeholder="e.g. 16000000"
                  onChange={e=>updGoal(i,e.target.value)}
                  style={{background:"#F0F0F0",border:`1px solid ${seg.goal?"rgba(204,26,26,0.10)":"rgba(0,0,0,0.08)"}`,borderRadius:7,padding:"7px 10px",color:"#111111",fontSize:12,fontFamily:"'DM Mono',monospace",outline:"none",width:"100%",boxSizing:"border-box"}}/>
              ) : (
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <input type="number" value={pct||""} placeholder="e.g. 76" min="0" max="100"
                    onChange={e=>updPct(i,e.target.value)}
                    style={{flex:1,background:"#F0F0F0",border:`1px solid ${seg.goal?"rgba(204,26,26,0.10)":"rgba(0,0,0,0.08)"}`,borderRadius:7,padding:"7px 10px",color:"#111111",fontSize:12,fontFamily:"'DM Mono',monospace",outline:"none",boxSizing:"border-box"}}/>
                  <span style={{fontSize:12,color:"#555555"}}>%</span>
                </div>
              )}

              {/* Computed amount */}
              <div>
                <div style={{fontSize:12,fontWeight:700,color:seg.goal?"#111111":"#555555",fontFamily:"'DM Mono',monospace"}}>{seg.goal?fmtM(seg.goal):"—"}</div>
                {annual > 0 && seg.goal && (
                  <div style={{marginTop:3,height:3,background:"rgba(0,0,0,0.08)",borderRadius:2,overflow:"hidden"}}>
                    <div style={{width:`${barPct}%`,height:"100%",background:seg.color||cols[i%cols.length],borderRadius:2}}/>
                  </div>
                )}
              </div>

              {/* Delete */}
              <button onClick={()=>upd("segments.list",list.filter((_,j)=>j!==i))}
                style={{background:"transparent",border:"none",padding:"6px 4px",cursor:"pointer",opacity:0.5,lineHeight:1}}>
                <X size={12} color="#DC2626"/>
              </button>
            </div>
          );
        })}
      </div>

      {/* Add segment */}
      <button onClick={()=>upd("segments.list",[...list,{name:"",goal:"",color:cols[list.length%cols.length]}])}
        style={{display:"flex",alignItems:"center",gap:6,background:"transparent",border:"1px dashed rgba(204,26,26,0.10)",borderRadius:7,padding:"7px 14px",color:"#CC1A1A",fontSize:12,cursor:"pointer",marginBottom:14}}>
        <Plus size={13}/> Add Segment
      </button>

      {/* Balance bar */}
      {annual > 0 && total > 0 && (
        <div style={{padding:"12px 14px",background:"#F0F0F0",borderRadius:8}}>
          {/* Stacked bar */}
          <div style={{display:"flex",height:8,borderRadius:4,overflow:"hidden",marginBottom:10,gap:1}}>
            {list.filter(sg=>+sg.goal>0).map((sg,i)=>(
              <div key={i} title={sg.name}
                style={{width:`${+sg.goal/annual*100}%`,background:sg.color||cols[i%cols.length],transition:"width 0.3s"}}/>
            ))}
            {total < annual && (
              <div style={{flex:1,background:"rgba(0,0,0,0.08)"}}/>
            )}
          </div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div style={{display:"flex",gap:16}}>
              <span style={{fontSize:11,color:"#555555"}}>Total: <span style={{color:balanced?"#16A34A":"#C2610C",fontWeight:700}}>{fmtM(total)}</span></span>
              <span style={{fontSize:11,color:"#555555"}}>Target: <span style={{color:"#CC1A1A",fontWeight:700}}>{fmtM(annual)}</span></span>
              {!balanced && <span style={{fontSize:11,color:"#C2610C"}}>{total<annual?`${fmtM(annual-total)} unallocated`:`${fmtM(total-annual)} over`}</span>}
            </div>
            {balanced && <span style={{fontSize:11,color:"#16A34A",fontWeight:700}}>✓ Fully allocated</span>}
          </div>
        </div>
      )}

      <SegmentAttributionMapper s={s} upd={upd}/>
    </>
  );
};

// ── Performance Targets Section ───────────────────
const DEFAULT_DEAL_TYPES = ["New Logo", "Upsell", "Renewal", "Cross-sell"];

const TEAM_BENCHMARKS = {
  "New Logo":   { winRate: "20–30", avgDealSize: "40000–80000", salesCycle: "60–120" },
  "Upsell":     { winRate: "35–55", avgDealSize: "15000–40000", salesCycle: "30–60"  },
  "Renewal":    { winRate: "70–90", avgDealSize: "20000–60000", salesCycle: "30–60"  },
  "Cross-sell": { winRate: "25–45", avgDealSize: "10000–30000", salesCycle: "30–60"  },
};

const PerformanceTargetsSection = ({ s, upd }) => {
  const reps = s.team?.reps || [];
  const teams = [...new Set(reps.map(r => r.team || r.segment).filter(Boolean))];
  const hasTeams = teams.length > 0;
  const [activeTeam, setActiveTeam] = React.useState(null);
  const [newDealType, setNewDealType] = React.useState("");
  const currentTeam = activeTeam || teams[0] || null;

  const byTeam = s.targets?.byTeam || {};

  // Per-team deal type lists stored in targets.dealTypes
  const getDealTypes = (team) => {
    return s.targets?.dealTypes?.[team] || DEFAULT_DEAL_TYPES;
  };

  const setDealTypes = (team, types) => {
    const updated = { ...(s.targets?.dealTypes || {}), [team]: types };
    upd("targets.dealTypes", updated);
    upd("targets.configured", true);
  };

  const removeDealType = (team, dt) => {
    setDealTypes(team, getDealTypes(team).filter(d => d !== dt));
    // Also clean up saved targets for that type
    const updated = { ...byTeam };
    if (updated[team]) {
      const t = { ...updated[team] };
      delete t[dt];
      updated[team] = t;
    }
    upd("targets.byTeam", updated);
  };

  const addDealType = (team, dt) => {
    const trimmed = dt.trim();
    if (!trimmed || getDealTypes(team).includes(trimmed)) return;
    setDealTypes(team, [...getDealTypes(team), trimmed]);
    setNewDealType("");
  };

  const updTarget = (team, dealType, field, val) => {
    const updated = {
      ...byTeam,
      [team]: {
        ...(byTeam[team] || {}),
        [dealType]: { ...((byTeam[team] || {})[dealType] || {}), [field]: val },
      },
    };
    upd("targets.byTeam", updated);
    upd("targets.configured", true);
    if (team === teams[0] && dealType === getDealTypes(team)[0]) {
      if (field === "winRate")     upd("targets.winRate", val);
      if (field === "avgDealSize") upd("targets.avgDealSize", val);
      if (field === "salesCycle")  upd("targets.salesCycle", val);
    }
  };

  const getTarget = (team, dealType, field) => byTeam?.[team]?.[dealType]?.[field] || "";

  const bench = (dealType) => TEAM_BENCHMARKS[dealType] || { winRate: "25–35", avgDealSize: "30000–70000", salesCycle: "60–90" };

  const teamColor = (t) => {
    const colors = ["#CC1A1A","#D97706","#16A34A","#7C3AED","#0284C7","#DB2777"];
    return colors[teams.indexOf(t) % colors.length];
  };

  return (
    <>
      <SH title="Performance Targets" sub="Set win rate, avg deal size, and sales cycle targets by team and deal type"/>

      {!hasTeams ? (
        <div style={{padding:"20px",background:"rgba(217,119,6,0.08)",border:"1px solid rgba(217,119,6,0.2)",borderRadius:10,textAlign:"center"}}>
          <div style={{fontSize:13,fontWeight:700,color:"#D97706",marginBottom:6}}>⚠ Set up your team first</div>
          <div style={{fontSize:12,color:"#555555"}}>Go to <strong>Quota & Team</strong> and assign each rep to a team (e.g. Enterprise, Mid-Market, SMB). Targets will be organised by those teams here.</div>
        </div>
      ) : (
        <div>
          {/* Team tabs */}
          <div style={{display:"flex",gap:6,marginBottom:20,flexWrap:"wrap"}}>
            {teams.map(t => {
              const active = currentTeam === t;
              const tc = teamColor(t);
              const repCount = reps.filter(r => (r.team||r.segment) === t).length;
              return (
                <button key={t} onClick={() => { setActiveTeam(t); setNewDealType(""); }}
                  style={{padding:"7px 16px",borderRadius:8,border:`1px solid ${active ? tc : "rgba(0,0,0,0.10)"}`,
                    background: active ? `${tc}12` : "#FFFFFF",
                    color: active ? tc : "#555555",fontSize:12,fontWeight:active?700:400,cursor:"pointer",
                    display:"flex",alignItems:"center",gap:6}}>
                  {t}
                  <span style={{fontSize:10,background:`${tc}18`,color:tc,borderRadius:10,padding:"1px 6px",fontWeight:700}}>{repCount} rep{repCount!==1?"s":""}</span>
                </button>
              );
            })}
          </div>

          {currentTeam && (() => {
            const dealTypes = getDealTypes(currentTeam);
            return (
              <div>
                <div style={{fontSize:11,fontWeight:700,color:"#555555",textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:14}}>
                  {currentTeam} Team — Targets by Deal Type
                </div>

                {/* Grid header */}
                <div style={{display:"grid",gridTemplateColumns:"150px repeat(3,1fr) 32px",gap:1,marginBottom:1}}>
                  <div style={{padding:"8px 12px",background:"#F0F0F0",borderRadius:"8px 0 0 0",fontSize:10,fontWeight:700,color:"#555555",textTransform:"uppercase",letterSpacing:"0.06em"}}>Deal Type</div>
                  {["Win Rate (%)","Avg Deal Size ($)","Sales Cycle (days)"].map((h,i) => (
                    <div key={i} style={{padding:"8px 12px",background:"#F0F0F0",fontSize:10,fontWeight:700,color:"#555555",textTransform:"uppercase",letterSpacing:"0.06em",borderRadius:i===2?"0 8px 0 0":"none"}}>{h}</div>
                  ))}
                  <div style={{background:"#F0F0F0",borderRadius:"0 8px 0 0"}}/>
                </div>

                {dealTypes.map((dt, di) => {
                  const b = bench(dt);
                  const isLast = di === dealTypes.length - 1;
                  const wr = getTarget(currentTeam, dt, "winRate");
                  const ds = getTarget(currentTeam, dt, "avgDealSize");
                  const sc = getTarget(currentTeam, dt, "salesCycle");
                  const filled = wr && ds && sc;
                  return (
                    <div key={dt} style={{display:"grid",gridTemplateColumns:"150px repeat(3,1fr) 32px",gap:1,marginBottom:1}}>
                      <div style={{padding:"12px",background:filled?"rgba(22,163,74,0.05)":"#FAFAFA",
                        border:"1px solid rgba(0,0,0,0.06)",borderRadius:isLast?"0 0 0 8px":"none",
                        display:"flex",alignItems:"center",gap:8}}>
                        <div style={{width:7,height:7,borderRadius:"50%",background:filled?"#16A34A":"#CCCCCC",flexShrink:0}}/>
                        <div style={{fontSize:12,fontWeight:700,color:"#111111",lineHeight:1.2}}>{dt}</div>
                      </div>
                      {[
                        { field:"winRate",     val:wr, bench:`${b.winRate}%`,     placeholder:b.winRate.split("–")[0], prefix:null, suffix:"%" },
                        { field:"avgDealSize", val:ds, bench:`$${b.avgDealSize}`, placeholder:b.avgDealSize.split("–")[0], prefix:"$", suffix:null },
                        { field:"salesCycle",  val:sc, bench:`${b.salesCycle}d`,  placeholder:b.salesCycle.split("–")[0], prefix:null, suffix:"days" },
                      ].map((col, ci) => (
                        <div key={col.field} style={{padding:"10px 12px",background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.06)"}}>
                          <div style={{display:"flex",alignItems:"center",gap:4}}>
                            {col.prefix && <span style={{fontSize:13,fontWeight:600,color:"#555",flexShrink:0}}>{col.prefix}</span>}
                            <input type="number" value={col.val} placeholder={col.placeholder}
                              onChange={e => updTarget(currentTeam, dt, col.field, e.target.value)}
                              style={{flex:1,minWidth:0,background:"#F0F0F0",border:`1px solid ${col.val?"rgba(204,26,26,0.25)":"rgba(0,0,0,0.08)"}`,
                                borderRadius:6,padding:"7px 10px",color:"#111111",fontSize:13,outline:"none",boxSizing:"border-box"}}/>
                            {col.suffix && <span style={{fontSize:12,fontWeight:600,color:"#555",flexShrink:0}}>{col.suffix}</span>}
                          </div>
                          <div style={{fontSize:9,color:"#888888",marginTop:3}}>Benchmark: {col.bench}</div>
                        </div>
                      ))}
                      {/* Delete row button */}
                      <div style={{display:"flex",alignItems:"center",justifyContent:"center",background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.06)",borderRadius:isLast?"0 0 8px 0":"none"}}>
                        <button onClick={() => removeDealType(currentTeam, dt)}
                          title="Remove this deal type"
                          style={{background:"none",border:"none",cursor:"pointer",padding:"4px",color:"#DC2626",fontSize:14,lineHeight:1,opacity:0.5}}
                          onMouseEnter={e=>e.currentTarget.style.opacity=1}
                          onMouseLeave={e=>e.currentTarget.style.opacity=0.5}>✕</button>
                      </div>
                    </div>
                  );
                })}

                {/* Add deal type row */}
                <div style={{display:"flex",alignItems:"center",gap:8,marginTop:8}}>
                  <input value={newDealType} onChange={e=>setNewDealType(e.target.value)}
                    onKeyDown={e=>e.key==="Enter"&&addDealType(currentTeam,newDealType)}
                    placeholder="Add deal type (e.g. Expansion)…"
                    list="dt-suggestions"
                    style={{flex:1,background:"#F0F0F0",border:"1px dashed rgba(0,0,0,0.15)",borderRadius:7,padding:"8px 12px",color:"#111111",fontSize:12,outline:"none"}}/>
                  <datalist id="dt-suggestions">
                    {["New Logo","Upsell","Renewal","Cross-sell","Expansion","Partner","Channel"].filter(d=>!dealTypes.includes(d)).map(d=><option key={d} value={d}/>)}
                  </datalist>
                  <button onClick={()=>addDealType(currentTeam,newDealType)}
                    disabled={!newDealType.trim()}
                    style={{padding:"8px 14px",borderRadius:7,border:"1px solid rgba(204,26,26,0.25)",background:"transparent",color:"#CC1A1A",fontSize:12,fontWeight:600,cursor:"pointer",opacity:newDealType.trim()?1:0.4}}>
                    + Add
                  </button>
                </div>

                {/* Reps on this team */}
                <div style={{marginTop:16,padding:"12px 16px",background:"#F8F8F8",borderRadius:8,border:"1px solid rgba(0,0,0,0.06)"}}>
                  <div style={{fontSize:10,fontWeight:700,color:"#555555",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>Reps on {currentTeam} team</div>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    {reps.filter(r=>(r.team||r.segment)===currentTeam).map((r,i)=>(
                      <div key={i} style={{fontSize:11,color:"#111111",background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.08)",borderRadius:6,padding:"4px 10px"}}>
                        {r.name||"Unnamed"}{r.quota?<span style={{color:"#555555"}}> · {fmtM(r.quota)}</span>:""}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Global targets */}
          <div style={{marginTop:24,paddingTop:20,borderTop:"1px solid rgba(0,0,0,0.08)"}}>
            <div style={{fontSize:11,fontWeight:700,color:"#555555",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:12}}>Global Targets</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12}}>
              <div>
                <div style={{fontSize:10,fontWeight:700,color:"#555555",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>NRR Target (%)</div>
                <input type="number" value={s.targets.nrr||""} placeholder="120"
                  onChange={e=>{upd("targets.nrr",e.target.value);upd("targets.configured",true);}}
                  style={{width:"100%",background:"#F0F0F0",border:`1px solid ${s.targets.nrr?"rgba(204,26,26,0.10)":"rgba(0,0,0,0.08)"}`,borderRadius:7,padding:"8px 12px",color:"#111111",fontSize:13,outline:"none",boxSizing:"border-box"}}/>
                <div style={{fontSize:10,color:"#555555",marginTop:4}}>Best-in-class: 120%+</div>
              </div>
              <div>
                <div style={{fontSize:10,fontWeight:700,color:"#555555",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>BDR Headcount</div>
                <input type="number" value={s.targets.bdrCount||""} placeholder="e.g. 3"
                  onChange={e=>{upd("targets.bdrCount",e.target.value);upd("targets.configured",true);}}
                  style={{width:"100%",background:"#F0F0F0",border:"1px solid rgba(0,0,0,0.08)",borderRadius:7,padding:"8px 12px",color:"#111111",fontSize:13,outline:"none",boxSizing:"border-box"}}/>
              </div>
              <div>
                <div style={{fontSize:10,fontWeight:700,color:"#555555",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>AE Headcount</div>
                <input type="number" value={s.targets.aeCount||""} placeholder="e.g. 5"
                  onChange={e=>{upd("targets.aeCount",e.target.value);upd("targets.configured",true);}}
                  style={{width:"100%",background:"#F0F0F0",border:"1px solid rgba(0,0,0,0.08)",borderRadius:7,padding:"8px 12px",color:"#111111",fontSize:13,outline:"none",boxSizing:"border-box"}}/>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};



const MONTHS = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];
const MONTH_LABELS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const QUARTERS = [
  { id:"q1", label:"Q1", months:["jan","feb","mar"] },
  { id:"q2", label:"Q2", months:["apr","may","jun"] },
  { id:"q3", label:"Q3", months:["jul","aug","sep"] },
  { id:"q4", label:"Q4", months:["oct","nov","dec"] },
];

const RAMP_PROFILES = {
  "Even":         [1,1,1,1,1,1,1,1,1,1,1,1],
  "Back-loaded":  [0.6,0.7,0.8,0.85,0.9,0.95,1,1.05,1.1,1.15,1.2,1.3],
  "Front-loaded": [1.3,1.2,1.15,1.1,1.05,1,0.95,0.9,0.85,0.8,0.7,0.6],
  "Q4 Heavy":     [0.7,0.75,0.8,0.85,0.88,0.9,0.92,0.95,1,1.15,1.25,1.4],
  "Seasonal":     [0.8,0.85,1.1,0.9,0.95,1.2,0.75,0.85,1.1,1,1.05,1.4],
};

const RevenueTargetSection = ({ s, upd }) => {
  const [showMonthly, setShowMonthly] = useState(!!(
    s.revenue?.monthlyTargets && Object.values(s.revenue.monthlyTargets||{}).some(v=>v)
  ));
  const annual = +s.revenue?.target || 0;
  const monthly = s.revenue?.monthlyTargets || {};

  const monthSum = MONTHS.reduce((a,m) => a + (+monthly[m]||0), 0);
  const diff = annual - monthSum;
  const allFilled = MONTHS.every(m => +monthly[m] > 0);

  const setMonth = (m, val) => {
    const updated = { ...monthly, [m]: val };
    upd("revenue.monthlyTargets", updated);
    // Auto-update annual if months are being entered and annual is empty
    if (!s.revenue.target) {
      const sum = MONTHS.reduce((a,mo) => a + (+updated[mo]||0), 0);
      if (sum > 0) { upd("revenue.target", String(sum)); upd("revenue.configured", true); }
    }
  };

  const applyRamp = (profileName) => {
    if (!annual) return;
    const weights = RAMP_PROFILES[profileName];
    const totalWeight = weights.reduce((a,w) => a+w, 0);
    const updated = {};
    MONTHS.forEach((m,i) => {
      updated[m] = String(Math.round(annual * weights[i] / totalWeight));
    });
    upd("revenue.monthlyTargets", updated);
  };

  const distributeEvenly = () => {
    if (!annual) return;
    const base = Math.floor(annual / 12);
    const remainder = annual - base * 12;
    const updated = {};
    MONTHS.forEach((m,i) => { updated[m] = String(base + (i < remainder ? 1 : 0)); });
    upd("revenue.monthlyTargets", updated);
  };

  const qTotal = (q) => q.months.reduce((a,m) => a + (+monthly[m]||0), 0);
  const maxMonth = Math.max(...MONTHS.map(m => +monthly[m]||0), 1);

  return (
    <>
      <SH title="Revenue Target" sub="Set your annual goal, then break it down by month for accurate period filtering"/>

      {/* Annual target */}
      <Inp label="Annual Revenue Target ($)" value={s.revenue.target} type="number"
        onChange={v => { upd("revenue.target", v); upd("revenue.configured", true); }}
        placeholder="e.g. 21000000" hint="Total bookings goal for the fiscal year"/>

      {s.revenue.target && (
        <div style={{background:"#F0F0F0",borderRadius:8,padding:"12px 16px",marginBottom:16}}>
          <div style={{display:"flex",gap:24,flexWrap:"wrap"}}>
            {[
              {l:"Annual Target",   v:fmtM(s.revenue.target)},
              {l:"3x Pipeline Need",v:fmtM(+s.revenue.target*3)},
              {l:"Avg Monthly",     v:fmtM(+s.revenue.target/12)},
              {l:"Avg Quarterly",   v:fmtM(+s.revenue.target/4)},
            ].map((r,i)=>(
              <div key={i}>
                <div style={{fontSize:10,color:"#555555"}}>{r.l}</div>
                <div style={{fontSize:18,fontWeight:700,color:"#CC1A1A",fontFamily:"'DM Mono',monospace"}}>{r.v}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Monthly toggle */}
      <button onClick={() => setShowMonthly(m => !m)}
        style={{display:"flex",alignItems:"center",gap:8,background:showMonthly?"rgba(204,26,26,0.10)":"transparent",
          border:`1px solid ${showMonthly?"rgba(204,26,26,0.10)":"rgba(0,0,0,0.08)"}`,
          borderRadius:8,padding:"8px 14px",color:showMonthly?"#CC1A1A":"#555555",fontSize:12,fontWeight:600,cursor:"pointer",marginBottom:14}}>
        <span style={{fontSize:14}}>{showMonthly?"▼":"▶"}</span>
        {showMonthly ? "Hide Monthly Breakdown" : "Set Monthly Targets"} 
        {allFilled && <span style={{fontSize:10,color:"#16A34A",marginLeft:4}}>✓ All months set</span>}
      </button>

      {showMonthly && (
        <div>
          {/* Quick distribute buttons */}
          {annual > 0 && (
            <div style={{marginBottom:14}}>
              <div style={{fontSize:10,fontWeight:700,color:"#555555",textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:8}}>Quick Distribution</div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                <button onClick={distributeEvenly}
                  style={{padding:"5px 12px",borderRadius:6,background:"rgba(204,26,26,0.10)",border:"1px solid rgba(204,26,26,0.10)",color:"#CC1A1A",fontSize:11,cursor:"pointer",fontWeight:600}}>
                  ÷ Distribute Evenly
                </button>
                {Object.keys(RAMP_PROFILES).filter(p=>p!=="Even").map(p=>(
                  <button key={p} onClick={()=>applyRamp(p)}
                    style={{padding:"5px 12px",borderRadius:6,background:"transparent",border:"1px solid rgba(0,0,0,0.08)",color:"#555555",fontSize:11,cursor:"pointer"}}
                    onMouseEnter={e=>{e.target.style.borderColor="#CC1A1A";e.target.style.color="#CC1A1A"}}
                    onMouseLeave={e=>{e.target.style.borderColor="rgba(0,0,0,0.08)";e.target.style.color="#555555"}}>
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Quarter sections */}
          {QUARTERS.map(q => (
            <div key={q.id} style={{marginBottom:16}}>
              {/* Quarter header */}
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8,
                padding:"7px 12px",background:"#FFFFFF",borderRadius:8,border:"1px solid rgba(0,0,0,0.08)"}}>
                <span style={{fontSize:12,fontWeight:700,color:"#CC1A1A"}}>{q.label}</span>
                <div style={{display:"flex",gap:16,alignItems:"center"}}>
                  <span style={{fontSize:12,fontWeight:700,color:"#111111",fontFamily:"'DM Mono',monospace"}}>{fmtM(qTotal(q))}</span>
                  {annual > 0 && <span style={{fontSize:10,color:"#555555"}}>{Math.round(qTotal(q)/annual*100)}% of annual</span>}
                  {annual > 0 && <span style={{fontSize:10,color:Math.abs(qTotal(q)-annual/4)<annual*0.02?"#16A34A":"#D97706"}}>
                    {qTotal(q) > annual/4 ? "+" : ""}{fmtM(qTotal(q)-annual/4)} vs even split
                  </span>}
                </div>
              </div>

              {/* Month inputs with mini bars */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
                {q.months.map((m,mi) => {
                  const val = +monthly[m] || 0;
                  const barPct = maxMonth > 0 ? val/maxMonth*100 : 0;
                  const mIdx = MONTHS.indexOf(m);
                  return (
                    <div key={m} style={{background:"#FFFFFF",borderRadius:8,padding:"10px 12px",border:`1px solid ${monthly[m]?"rgba(204,26,26,0.10)":"rgba(0,0,0,0.08)"}`}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                        <span style={{fontSize:11,fontWeight:700,color:"#111111"}}>{MONTH_LABELS[mIdx]}</span>
                        {annual > 0 && val > 0 && <span style={{fontSize:9,color:"#555555"}}>{Math.round(val/annual*100)}%</span>}
                      </div>
                      {/* Mini bar */}
                      <div style={{height:3,background:"rgba(0,0,0,0.08)",borderRadius:2,marginBottom:8,overflow:"hidden"}}>
                        <div style={{width:`${barPct}%`,height:"100%",background:"#CC1A1A",borderRadius:2,transition:"width 0.3s"}}/>
                      </div>
                      <input
                        type="number"
                        value={monthly[m]||""}
                        onChange={e => setMonth(m, e.target.value)}
                        placeholder="e.g. 1750000"
                        style={{width:"100%",background:"transparent",border:"none",borderBottom:"1px solid rgba(204,26,26,0.10)",
                          color:"#111111",fontSize:12,fontFamily:"'DM Mono',monospace",outline:"none",padding:"2px 0",boxSizing:"border-box"}}
                      />
                      {val > 0 && <div style={{fontSize:10,color:"#CC1A1A",marginTop:4,fontFamily:"'DM Mono',monospace"}}>{fmtM(val)}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Summary footer */}
          <div style={{padding:"12px 16px",background:"#F0F0F0",borderRadius:8,display:"flex",gap:24,alignItems:"center",flexWrap:"wrap"}}>
            <div>
              <div style={{fontSize:10,color:"#555555"}}>Monthly total</div>
              <div style={{fontSize:16,fontWeight:700,color:Math.abs(diff)<100?"#16A34A":"#D97706",fontFamily:"'DM Mono',monospace"}}>{fmtM(monthSum)}</div>
            </div>
            <div>
              <div style={{fontSize:10,color:"#555555"}}>Annual target</div>
              <div style={{fontSize:16,fontWeight:700,color:"#CC1A1A",fontFamily:"'DM Mono',monospace"}}>{fmtM(annual)}</div>
            </div>
            {monthly && monthSum > 0 && (
              <div>
                <div style={{fontSize:10,color:"#555555"}}>Difference</div>
                <div style={{fontSize:16,fontWeight:700,color:Math.abs(diff)<100?"#16A34A":"#DC2626",fontFamily:"'DM Mono',monospace"}}>
                  {diff > 0 ? "-" : "+"}{fmtM(Math.abs(diff))}
                </div>
              </div>
            )}
            {Math.abs(diff) < 100 && monthSum > 0 && (
              <div style={{marginLeft:"auto",fontSize:12,color:"#16A34A",fontWeight:700}}>✓ Monthly targets balanced</div>
            )}
            {Math.abs(diff) >= 100 && monthSum > 0 && annual > 0 && (
              <div style={{marginLeft:"auto",fontSize:11,color:"#D97706"}}>
                Monthly total {diff > 0 ? "under" : "over"} annual by {fmtM(Math.abs(diff))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};


const STAGE_TYPES = [
  { value: "pipeline",   label: "Active Pipeline", color: "#CC1A1A",  desc: "Counts toward coverage ratio" },
  { value: "won",        label: "Closed Won",       color: "#16A34A",  desc: "Used for win rate & deal size" },
  { value: "lost",       label: "Closed Lost",      color: "#DC2626",  desc: "Used for win rate denominator" },
  { value: "ignore",     label: "Ignore",           color: "#555555",  desc: "Excluded from all calculations" },
];

const StageMapper = ({ s, upd }) => {
  const [hsStages,    setHsStages]    = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState("");
  const [stageTypes,  setStageTypes]  = useState(() => {
    // Initialize from saved settings
    const saved = {};
    (s.stages?.mappings || []).forEach(m => { saved[m.id] = m.type; });
    return saved;
  });

  const loadStages = async () => {
    if (!s.hubspot?.token) { setError("Connect HubSpot first — add your token in the HubSpot Connection section."); return; }
    setLoading(true);
    setError("");
    try {
      const stages = await fetchPipelineStages(s.hubspot.token);
      if (!stages.length) { setError("No pipeline stages found. Check your HubSpot token has crm.objects.deals.read scope."); return; }
      setHsStages(stages);
      // Auto-detect won/lost by probability
      const autoTypes = {...stageTypes};
      stages.forEach(st => {
        if (autoTypes[st.id]) return; // don't overwrite manual selections
        const prob = parseFloat(st.probability);
        const labelLower = st.label.toLowerCase();
        if (prob === 1 || labelLower.includes("won") || labelLower.includes("closed won")) autoTypes[st.id] = "won";
        else if (prob === 0 || labelLower.includes("lost") || labelLower.includes("closed lost")) autoTypes[st.id] = "lost";
        else autoTypes[st.id] = "pipeline";
      });
      setStageTypes(autoTypes);
      saveMappings(autoTypes, stages);
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const saveMappings = (types, stages) => {
    const src = stages.length ? stages : hsStages;
    const mappings = src.map(st => ({ id: st.id, label: st.label, type: types[st.id] || "pipeline" }));
    upd("stages.mappings", mappings);
    upd("stages.list", [
      { label: "Closed Won",  id: mappings.filter(m=>m.type==="won").map(m=>m.id).join(",") },
      { label: "Closed Lost", id: mappings.filter(m=>m.type==="lost").map(m=>m.id).join(",") },
    ]);
    upd("stages.configured", mappings.length > 0);
  };

  const setType = (stageId, type) => {
    const updated = { ...stageTypes, [stageId]: type };
    setStageTypes(updated);
    saveMappings(updated, hsStages);
  };

  const byPipeline = {};
  hsStages.forEach(st => {
    if (!byPipeline[st.pipeline]) byPipeline[st.pipeline] = [];
    byPipeline[st.pipeline].push(st);
  });

  const summary = {
    pipeline: Object.values(stageTypes).filter(t=>t==="pipeline").length,
    won:      Object.values(stageTypes).filter(t=>t==="won").length,
    lost:     Object.values(stageTypes).filter(t=>t==="lost").length,
    ignore:   Object.values(stageTypes).filter(t=>t==="ignore").length,
  };

  return (
    <>
      <SH title="HubSpot Stage Mapping" sub="Pull your live stages and tag each one — this drives win rate, deal size, and pipeline coverage"/>

      {/* Summary badges */}
      {hsStages.length > 0 && (
        <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap"}}>
          {STAGE_TYPES.map(t=>(
            <div key={t.value} style={{display:"flex",alignItems:"center",gap:6,background:`${t.color}12`,border:`1px solid ${t.color}30`,borderRadius:20,padding:"4px 12px",fontSize:11,color:t.color,fontWeight:600}}>
              {summary[t.value]} {t.label}
            </div>
          ))}
        </div>
      )}

      {/* Load button */}
      {!hsStages.length && (
        <div style={{marginBottom:16}}>
          <button onClick={loadStages} disabled={loading}
            style={{background:"linear-gradient(135deg,#CC1A1A,#991B1B)",border:"none",borderRadius:8,padding:"10px 20px",color:"#F8F8F8",fontSize:13,fontWeight:700,cursor:"pointer",opacity:loading?0.7:1}}>
            {loading ? "Loading stages…" : "🔄 Load Stages from HubSpot"}
          </button>
          {error && <div style={{marginTop:8,fontSize:12,color:"#DC2626"}}>{error}</div>}
          {/* Show saved mappings if they exist */}
          {(s.stages?.mappings||[]).length > 0 && !hsStages.length && (
            <div style={{marginTop:12,padding:"10px 14px",background:"rgba(204,26,26,0.10)",borderRadius:8,fontSize:12,color:"#CC1A1A"}}>
              ✓ {s.stages.mappings.length} stages saved from last session — click Load to refresh
            </div>
          )}
        </div>
      )}

      {/* Stage table */}
      {hsStages.length > 0 && (
        <>
          {Object.entries(byPipeline).map(([pipelineName, stages]) => (
            <div key={pipelineName} style={{marginBottom:20}}>
              <div style={{fontSize:11,fontWeight:700,color:"#555555",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8,paddingBottom:6,borderBottom:"1px solid rgba(0,0,0,0.08)"}}>
                Pipeline: {pipelineName}
              </div>
              {stages.map(stage => {
                const type = stageTypes[stage.id] || "pipeline";
                const typeInfo = STAGE_TYPES.find(t=>t.value===type);
                return (
                  <div key={stage.id} style={{display:"grid",gridTemplateColumns:"1fr auto",alignItems:"center",gap:12,padding:"10px 12px",marginBottom:4,background:"#FFFFFF",borderRadius:8,border:`1px solid ${typeInfo.color}20`}}>
                    <div>
                      <div style={{fontSize:13,fontWeight:600,color:"#111111"}}>{stage.label}</div>
                      <div style={{fontSize:10,color:"#555555",marginTop:2}}>
                        ID: {stage.id} · Probability: {Math.round(parseFloat(stage.probability)*100)}%
                      </div>
                    </div>
                    <div style={{display:"flex",gap:6}}>
                      {STAGE_TYPES.map(t=>(
                        <button key={t.value} onClick={()=>setType(stage.id, t.value)}
                          style={{
                            padding:"5px 10px",borderRadius:6,border:`1px solid ${type===t.value?t.color:"rgba(0,0,0,0.08)"}`,
                            background:type===t.value?`${t.color}20`:"transparent",
                            color:type===t.value?t.color:"#555555",fontSize:11,fontWeight:type===t.value?700:400,cursor:"pointer",
                            transition:"all 0.15s"
                          }}>
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
          <button onClick={loadStages}
            style={{background:"transparent",border:"1px solid rgba(204,26,26,0.10)",borderRadius:7,padding:"7px 14px",color:"#CC1A1A",fontSize:11,cursor:"pointer",marginTop:4}}>
            🔄 Reload from HubSpot
          </button>
        </>
      )}

      {/* Legend */}
      <div style={{marginTop:16,padding:"12px 14px",background:"#F0F0F0",borderRadius:8}}>
        <div style={{fontSize:11,fontWeight:700,color:"#555555",marginBottom:8,textTransform:"uppercase",letterSpacing:"0.07em"}}>How each type is used</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:8}}>
          {STAGE_TYPES.map(t=>(
            <div key={t.value} style={{display:"flex",alignItems:"center",gap:8}}>
              <div style={{width:8,height:8,borderRadius:"50%",background:t.color,flexShrink:0}}/>
              <div>
                <span style={{fontSize:11,fontWeight:700,color:t.color}}>{t.label}</span>
                <span style={{fontSize:11,color:"#555555"}}> — {t.desc}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};


const SettingsScreen = ({settings,onSave}) => {
  const [s,setS] = useState(settings);
  const [sec,setSec] = useState("hubspot");
  const [saved,setSaved] = useState(false);


  const upd = (path,val) => {
    const parts = path.split(".");
    setS(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      let o = next;
      for(let i=0;i<parts.length-1;i++) {
        if (o[parts[i]] === undefined || o[parts[i]] === null) o[parts[i]] = {};
        o = o[parts[i]];
      }
      o[parts[parts.length-1]] = val;
      // Auto-save every change so crashes never wipe settings
      try { localStorage.setItem("rx_settings", JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const handleSave = () => { onSave(s); setSaved(true); setTimeout(()=>setSaved(false),2000); };

  // Settings = technical config only.
  // Revenue targets, team, segments, performance etc. all live in Setup (AnnualPlan).
  const SECS = [
    {id:"hubspot",  label:"HubSpot Connection", icon:"🔌", ok: s.hubspot?.configured},
    {id:"stages",   label:"Stage Mapping",       icon:"🔗", ok: s.stages.configured},
    {id:"fields",   label:"Field Mapping",       icon:"🗺️", ok: s.fieldMap?.configured},
    {id:"company",  label:"Company Info",        icon:"🏢", ok: !!s.company.name},
  ];

  return (
    <div className="fade-up" style={{display:"grid",gridTemplateColumns:"220px 1fr",gap:16}}>
      {/* Left nav */}
      <div>
        <div style={{fontSize:11,fontWeight:700,color:"#555555",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>Settings</div>
        {SECS.map(item=>(
          <button key={item.id} onClick={()=>setSec(item.id)}
            style={{display:"flex",alignItems:"center",justifyContent:"space-between",width:"100%",padding:"10px 12px",borderRadius:8,border:"none",cursor:"pointer",marginBottom:4,background:sec===item.id?"rgba(204,26,26,0.10)":"transparent",borderLeft:sec===item.id?"2px solid #CC1A1A":"2px solid transparent"}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{fontSize:14}}>{item.icon}</span>
              <span style={{fontSize:12,fontWeight:500,color:sec===item.id?"#CC1A1A":"#555555"}}>{item.label}</span>
            </div>
            {item.ok ? <CheckCircle size={13} color="#16A34A"/> : <div style={{width:8,height:8,borderRadius:"50%",background:"rgba(217,119,6,0.12)"}}/>}
          </button>
        ))}
        <button onClick={handleSave} style={{width:"100%",marginTop:16,background:saved?"#16A34A":"#CC1A1A",color:"#F8F8F8",border:"none",borderRadius:8,padding:"11px",fontSize:13,fontWeight:700,cursor:"pointer"}}>
          {saved?"✓ Saved!":"Save All Settings"}
        </button>
        <button onClick={()=>{if(window.confirm("Reset all settings?")){ onSave(DEFAULT); setS(DEFAULT); window.location.reload(); }}}
          style={{width:"100%",marginTop:8,background:"transparent",color:"#555555",border:"1px solid rgba(0,0,0,0.08)",borderRadius:8,padding:"9px",fontSize:12,cursor:"pointer"}}>
          Reset All
        </button>
      </div>

      {/* Content */}
      <Card>
        {sec==="company" && <>
          <SH title="Company Info" sub="Basic details used across the platform"/>
          <Inp label="Client / Company Name" value={s.company.name} onChange={v=>upd("company.name",v)} placeholder="e.g. Acme Corp"/>
          <Inp label="CRM Platform" value={s.company.crm} onChange={v=>upd("company.crm",v)} placeholder="HubSpot"/>
          <Inp label="Fiscal Year" value={s.company.fiscalYear} type="number" onChange={v=>upd("company.fiscalYear",v)} placeholder={String(new Date().getFullYear())}/>
          <div style={{marginTop:16,padding:"10px 14px",background:"rgba(0,0,0,0.03)",borderRadius:8,fontSize:11,color:"#888"}}>
            💡 Revenue targets, team setup, stage goals and performance targets all live in <strong>Setup</strong> — accessible from the left nav.
          </div>
        </>}

        {sec==="stages" && <StageMapper s={s} upd={upd}/>}
        {sec==="fields" && <FieldMapper s={s} upd={upd}/>}

        {sec==="hubspot" && <>
          <SH title="HubSpot Connection" sub="Paste your Private App token to connect live deal data"/>
          <div style={{padding:"12px 16px",background:"rgba(204,26,26,0.06)",border:"1px solid rgba(204,26,26,0.12)",borderRadius:8,fontSize:12,color:"#CC1A1A",marginBottom:16,lineHeight:1.8}}>
            💡 To get your token: HubSpot → Settings → Integrations → Private Apps → Create app<br/>
            Required scopes: <strong>crm.objects.deals.read · crm.objects.owners.read · crm.objects.companies.read</strong>
          </div>
          <Inp label="Private App Token" value={s.hubspot?.token||""} placeholder="pat-na1-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            hint="Stored only in your browser — never sent to any server except HubSpot directly"
            onChange={v=>{upd("hubspot.token",v);upd("hubspot.configured",!!v);}}/>
          <Inp label="HubSpot Portal ID (optional)" value={s.hubspot?.portalId||""} placeholder="e.g. 12345678"
            hint="Found in your HubSpot URL: app.hubspot.com/contacts/XXXXXXXX"
            onChange={v=>upd("hubspot.portalId",v)}/>
          {s.hubspot?.token && (
            <div style={{padding:"10px 14px",background:"rgba(22,163,74,0.08)",border:"1px solid rgba(22,163,74,0.20)",borderRadius:8,fontSize:12,color:"#16A34A",marginTop:8}}>
              ✓ Token saved — use the Reload button in the sidebar to pull live data
            </div>
          )}
        </>}
      </Card>
    </div>
  );
};



export { StageMapper, FieldMapper };
export default SettingsScreen;
